import { useMutation } from "@apollo/client";
import { useCallback, useRef } from "react";

import { analyticsService } from "../../../lib/analytics";
import { sentryService } from "../../../lib/sentry";
import { acceptTermsMutation } from "./queries";

// Version identifier of the Terms/Privacy the user accepts (a FE constant);
// the server stamps the time.
export const TERMS_VERSION = "2026-06-01";

// Single-flight: concurrent callers share one in-flight request. A failed
// attempt clears, so the next submit retries; a success latches, so a second
// submit after a partial failure doesn't re-affirm.
export function useAcceptTerms() {
  const [acceptTerms] = useMutation(acceptTermsMutation);
  const acceptedRef = useRef(false);
  const pendingRef = useRef<Promise<boolean> | null>(null);

  const requestAcceptance = useCallback((): Promise<boolean> => {
    if (acceptedRef.current) {
      return Promise.resolve(true);
    }
    if (pendingRef.current) {
      return pendingRef.current;
    }
    const attempt = acceptTerms({
      variables: { input: { termsVersion: TERMS_VERSION } },
    })
      .then(() => {
        acceptedRef.current = true;
        analyticsService.trackEvent("TermsAccepted", {
          termsVersion: TERMS_VERSION,
        });
        return true;
      })
      .catch(error => {
        sentryService.captureException(error);
        return false;
      })
      .finally(() => {
        pendingRef.current = null;
      });
    pendingRef.current = attempt;
    return attempt;
  }, [acceptTerms]);

  return { requestAcceptance };
}
